import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useNRLazyQuery from '../../../../graphql/useNRLazyQuery';
import useNRMutation from '../../../../graphql/UseNRMutation';
import { GET_JIRA_INTEGRATION, GET_VULNERABILITY_JIRA_TICKETS, QL_JIRA_INTEGRATION_RECONNECT } from '../../../../graphql/queries/jiraIntegration';
import JiraIcon from '../../../../NRIcons/IntegrationIcons/JiraIcon';
import { FiltersContext } from '../../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import { RBAC_PERMISSIONS } from '../../../../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../../../../pages/NRSettings/NRUsersAndAccess/rbac.constants';
import { CONSTANTS } from '../../../../constants/constants';
import WithPermissions from '../../../Permissions/WithPermissions';
import {
  JIRA_INTEGRATION_STATUS,
  JIRA_INTEGRATION_STATUS_LABELS
} from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraIntegration.constants';
import {
  JIRA_CONNECTION_HEALTH_LABELS,
  JIRA_SYSTEM_CHECK_STATUS
} from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraIntegrationDetail.constants';
import NRJiraReconnectModalContainer from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraReconnectModalContainer';
import NRJiraSystemCheckIcon from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraSystemCheckIcon';
import useCanManageJiraIntegration from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/useCanManageJiraIntegration';
import { CREATE_JIRA_TICKET_LABELS } from './createJiraTicket.constants';
import NRCreateVulnerabilityJiraTicket from './NRCreateVulnerabilityJiraTicket';
import NRJiraDisconnectedTicketsAlert from './NRJiraDisconnectedTicketsAlert';
import NRJiraTicketsTable from './NRJiraTicketsTable';
import {
  getTicketCountLabel,
  VULNERABILITY_JIRA_TICKETS_LABELS,
  VULNERABILITY_JIRA_TICKETS_POLL_INTERVAL_MS
} from './vulnerabilityJiraTickets.constants';

function NRActiveVulnerabilityJiraTickets({
  advisoryId,
  assetId,
  vulnerabilityData,
  cveDetails,
  sx,
  expandJiraTickets = false,
  isDisconnected = false,
  canDirectReconnect = false
}) {
  const theme = useTheme();
  const { drawerContent, setDrawerContent, drawerTitle, setDrawerTitle, setHasBack, setOpenDrawer } = useContext(FiltersContext);
  const canManageJiraIntegration = useCanManageJiraIntegration();
  const [isReconnectModalOpen, setIsReconnectModalOpen] = useState(false);
  const [isReconnectPending, setIsReconnectPending] = useState(false);

  const ticketArgs = useMemo(
    () => ({
      assetId,
      advisoryId,
      componentId: vulnerabilityData.componentId
    }),
    [advisoryId, assetId, vulnerabilityData.componentId]
  );

  const [tickets, ticketsLoading, , fetchTickets, , , startPolling, stopPolling] = useNRLazyQuery({
    query: GET_VULNERABILITY_JIRA_TICKETS,
    options: { fetchPolicy: 'network-only' },
    qlObjKey: 'vulnerabilityJiraTickets',
    defaultValue: []
  });

  const fetchTicketsRef = useRef(fetchTickets);
  fetchTicketsRef.current = fetchTickets;
  const startPollingRef = useRef(startPolling);
  startPollingRef.current = startPolling;
  const stopPollingRef = useRef(stopPolling);
  stopPollingRef.current = stopPolling;

  const [reconnectJira, , isReconnecting, reconnectError] = useNRMutation(
    QL_JIRA_INTEGRATION_RECONNECT,
    'jiraIntegrationReconnect',
    JIRA_CONNECTION_HEALTH_LABELS.RECONNECT_SUCCESS,
    JIRA_CONNECTION_HEALTH_LABELS.RECONNECT_ERROR,
    { refetchQueries: [{ query: GET_JIRA_INTEGRATION }] }
  );

  // BE rejects vulnerabilityJiraTickets without an enabled installation — skip while disconnected.
  useEffect(() => {
    if (isDisconnected) {
      stopPollingRef.current();
      return undefined;
    }

    fetchTicketsRef.current({
      variables: { args: ticketArgs }
    });
    return undefined;
  }, [isDisconnected, ticketArgs]);

  useEffect(() => {
    if (isDisconnected || !tickets.length) {
      stopPollingRef.current();
      return undefined;
    }

    startPollingRef.current(VULNERABILITY_JIRA_TICKETS_POLL_INTERVAL_MS);
    return () => {
      stopPollingRef.current();
    };
  }, [isDisconnected, tickets.length]);

  useEffect(() => {
    if (!isDisconnected) {
      setIsReconnectPending(false);
    }
  }, [isDisconnected]);

  useEffect(() => {
    if (reconnectError && !isReconnecting) {
      setIsReconnectPending(false);
    }
  }, [reconnectError, isReconnecting]);

  const handleCreateTicket = useCallback(
    event => {
      event?.stopPropagation();

      if (isDisconnected) {
        return;
      }

      if (drawerContent.length) {
        setHasBack(true);
      }

      setDrawerTitle([...drawerTitle, CREATE_JIRA_TICKET_LABELS.DRAWER_TITLE]);
      setDrawerContent([
        ...drawerContent,
        <NRCreateVulnerabilityJiraTicket
          key="create-jira-ticket"
          vulnerabilityData={vulnerabilityData}
          cveDetails={cveDetails}
          assetId={assetId}
          advisoryId={advisoryId}
        />
      ]);
      setOpenDrawer(true);
    },
    [
      advisoryId,
      assetId,
      cveDetails,
      drawerContent,
      drawerTitle,
      isDisconnected,
      setDrawerContent,
      setDrawerTitle,
      setHasBack,
      setOpenDrawer,
      vulnerabilityData
    ]
  );

  const handleReconnect = useCallback(
    event => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (isReconnecting || isReconnectPending) {
        return;
      }

      if (canDirectReconnect) {
        setIsReconnectPending(true);
        reconnectJira();
        return;
      }

      setIsReconnectModalOpen(true);
    },
    [canDirectReconnect, isReconnectPending, isReconnecting, reconnectJira]
  );

  const handleReconnectModalComplete = useCallback(() => {
    setIsReconnectModalOpen(false);
  }, []);

  const storedTicketCount = vulnerabilityData.jiraTicketCount || 0;
  const displayedTicketCount = isDisconnected ? storedTicketCount : tickets.length || storedTicketCount;
  const ticketCountLabel = displayedTicketCount ? getTicketCountLabel(displayedTicketCount) : VULNERABILITY_JIRA_TICKETS_LABELS.NO_TICKETS;
  const showAccordion = isDisconnected || displayedTicketCount > 0;
  const showTicketsTable = !isDisconnected && tickets.length > 0;
  const showTicketsLoading = !isDisconnected && ticketsLoading && tickets.length === 0;
  const showInitialLoading = showTicketsLoading && !showAccordion;
  const isReconnectInProgress = isReconnecting || isReconnectPending;

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    mt: theme.spacing(2)
  };

  const sectionHeaderContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Avatar
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.iconInfo
          }}
        >
          <JiraIcon width={theme.spacing(4)} height={theme.spacing(4)} />
        </Avatar>
        <Box sx={{ ml: theme.spacing(1), minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), minWidth: 0 }}>
            <Typography variant="label" color="text.secondary">
              {VULNERABILITY_JIRA_TICKETS_LABELS.SECTION_LABEL}
            </Typography>
            {isDisconnected && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: theme.spacing(0.5), flexShrink: 0 }}>
                <NRJiraSystemCheckIcon status={JIRA_SYSTEM_CHECK_STATUS.ERROR} size={20} />
                <Typography component="span" variant="body2" color="error.main">
                  {JIRA_INTEGRATION_STATUS_LABELS[JIRA_INTEGRATION_STATUS.NOT_CONNECTED]}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="h3" color="text.primary">
            {ticketCountLabel}
          </Typography>
        </Box>
      </Box>
      <WithPermissions
        permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}
        rbacPermission={RBAC_PERMISSIONS.JIRA_TICKETS_CREATE}
        resourceType={ACR_RESOURCE_TYPE.ASSET}
      >
        <Box onClick={event => event.stopPropagation()} sx={{ flexShrink: 0, ml: theme.spacing(2) }}>
          <Button variant="outlined" color="secondary" disabled={isDisconnected} onClick={handleCreateTicket}>
            {VULNERABILITY_JIRA_TICKETS_LABELS.CREATE_TICKET}
          </Button>
        </Box>
      </WithPermissions>
    </Box>
  );

  return (
    <Box sx={[sectionSx, sx]}>
      {showInitialLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: theme.spacing(3)
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : showAccordion ? (
        <Accordion
          defaultExpanded={expandJiraTickets}
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: theme.spacing(2),
              py: theme.spacing(2),
              '& .MuiAccordionSummary-content': {
                margin: 0,
                width: '100%'
              }
            }}
          >
            {sectionHeaderContent}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {isDisconnected && (
              <Box sx={{ px: theme.spacing(2), pb: theme.spacing(2) }}>
                <NRJiraDisconnectedTicketsAlert
                  onReconnect={canManageJiraIntegration ? handleReconnect : undefined}
                  isReconnecting={isReconnectInProgress}
                />
              </Box>
            )}
            {showTicketsLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: theme.spacing(3)
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {showTicketsTable && <NRJiraTicketsTable tickets={tickets} />}
          </AccordionDetails>
        </Accordion>
      ) : (
        <Box sx={{ p: theme.spacing(2) }}>{sectionHeaderContent}</Box>
      )}
      {canManageJiraIntegration && (
        <NRJiraReconnectModalContainer
          open={isReconnectModalOpen}
          onClose={() => setIsReconnectModalOpen(false)}
          onComplete={handleReconnectModalComplete}
        />
      )}
    </Box>
  );
}

export default NRActiveVulnerabilityJiraTickets;
